import type React from 'react';

import Link from 'next/link';

import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import type { DealershipsDealer } from '@core/Entities/Vehicle/Dealer.entity';
import { capitalCaseOnly } from '@core/Utils/Strings/CapitalCase';
import { Car } from '@gds/Icons/Paths/Car';
import { Location } from '@gds/Icons/Paths/Location';
import { Typography } from '@gds/Typography/Typography';
import { createSlug } from '@kentico/Utils/CreateSlug';

import { DealerLogo } from 'Components/DealerInformation/DealerLogo';

import styles from './DealersSectionWrapper.module.css';

interface DealerProps {
  dealer: DealershipsDealer;
  locale: Locale;
  carsLabel: string;
  tenant?: Tenant;
}

export const DealersCard: React.FC<DealerProps> = ({
  dealer: { displayName, logoUrl, name, postcode, city, street, listingsCount, dealerId },
  locale,
  carsLabel,
  tenant,
}) => {
  const baseUrl = `${!tenant ? `/${locale}` : ''}`;

  // Check if locale already contains language prefix
  const localePrefix = locale.startsWith(`/${locale}`) ? '' : `${baseUrl}`;
  const slug = `${createSlug(name!)}-${dealerId}`;
  const linkUrl = `${localePrefix}/dealerships/${slug}`;

  const dealerName = displayName || name;

  return (
    <Link href={linkUrl}>
      <section className={styles.dealerSection}>
        <div className={styles.headerWithLogo}>
          <DealerLogo name={dealerName || ''} logo={logoUrl ?? ''} />
          <Typography variant="h5" Component="h2" className={styles.capitalizeLabel}>
            {capitalCaseOnly(dealerName ?? '')}
          </Typography>
        </div>
        <div className={styles.headerWithIcon}>
          <Location />
          <Typography variant="subheading2" className={styles.capitalizeLabel}>
            {street ? `${capitalCaseOnly(street)},` : ''} {''}
            {postcode} {''} {capitalCaseOnly(city)}
          </Typography>
        </div>
        {!!listingsCount && (
          <div className={styles.headerWithIcon}>
            <Car />
            <Typography variant="subheading2">{`${listingsCount} ${carsLabel}`}</Typography>
          </div>
        )}
      </section>
    </Link>
  );
};
